import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Table, Badge, Button, Alert, Card, Row, Col } from 'react-bootstrap';
import { trackDelivery, fetchUserDeliveries } from '../store/slices/deliverySlice';
import { FaMapMarkerAlt, FaTruck, FaCheckCircle, FaUser, FaPhone, FaCar, FaClock, FaBoxOpen, FaArrowRight } from 'react-icons/fa';
import DeliveryOverviewStrip from './delivery/DeliveryOverviewStrip';
import PaymentDetails from './delivery/PaymentDetails';
import DeliveryStatusBadge from './delivery/DeliveryStatusBadge';
import { HistorySkeleton, TrackingSkeleton } from './delivery/DeliverySkeletons';
import DriverLocationMap from './DriverLocationMap';
import {
  formatDisplayOrderId,
  getDeliveryIdentifier,
  getStatusCopy,
  getTimelineBadgeClass,
  isDeliveredStatus,
} from '../utils/deliveryDisplay';
import '../styles/DeliveryList.css';

const getDisplayCost = (...values) => {
  const normalizedValues = values
    .filter((value) => value !== undefined && value !== null && value !== '')
    .map((value) => (typeof value === 'string' ? Number(value.replace(/[^\d.-]/g, '')) : Number(value)))
    .filter(Number.isFinite);
  const amount = normalizedValues.find((value) => value > 0) ?? normalizedValues[0];

  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
};

const getDeliveryIdentityValues = (delivery) => (
  [
    getDeliveryIdentifier(delivery),
    delivery?.id,
    delivery?.deliveryId,
    delivery?.delivery_id,
    delivery?.orderId,
    delivery?.order_id,
    delivery?._id,
  ]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .map(String)
);

const readPendingTrackIds = () => {
  const ids = [];

  if (Array.isArray(window.history.state?.usr?.trackDeliveryIds)) {
    ids.push(...window.history.state.usr.trackDeliveryIds);
  }

  if (window.history.state?.usr?.trackDeliveryId) {
    ids.push(window.history.state.usr.trackDeliveryId);
  }

  const storedIds = window.sessionStorage.getItem('pendingTrackDeliveryIds');
  if (storedIds) {
    try {
      const parsedIds = JSON.parse(storedIds);
      if (Array.isArray(parsedIds)) {
        ids.push(...parsedIds);
      }
    } catch {
      window.sessionStorage.removeItem('pendingTrackDeliveryIds');
    }
  }

  const storedId = window.sessionStorage.getItem('pendingTrackDeliveryId');
  if (storedId) {
    ids.push(storedId);
  }

  return [...new Set(ids.filter(Boolean).map(String))];
};

const getDeliveryCreatedTime = (delivery) => {
  const dateValue =
    delivery?.createdAt ||
    delivery?.created_at ||
    delivery?.createdDate ||
    delivery?.created_date ||
    delivery?.timestamp ||
    delivery?.orderDate ||
    delivery?.order_date ||
    '';
  const time = new Date(dateValue).getTime();

  return Number.isFinite(time) ? time : 0;
};

const DeliveryList = ({ view = 'history' }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { deliveries, ordersLoading: loading, ordersError: error, trackingData } = useSelector(
    (state) => state.delivery
  );
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);
  const inlineTrackingRef = useRef(null);
  const initialTrackHandledRef = useRef(false);
  const shouldFocusTrackingRef = useRef(false);
  const sortedDeliveries = useMemo(
    () => [...deliveries].sort((a, b) => getDeliveryCreatedTime(b) - getDeliveryCreatedTime(a)),
    [deliveries]
  );

  // Wait for each response before scheduling the next request.
  useEffect(() => {
    let stopped = false;
    let timer;
    const poll = async () => {
      await dispatch(fetchUserDeliveries());
      if (!stopped) timer = window.setTimeout(poll, 5000);
    };
    poll();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!selectedDeliveryId) return;
    let stopped = false;
    let timer;
    const poll = async () => {
      await dispatch(trackDelivery(selectedDeliveryId));
      if (!stopped) timer = window.setTimeout(poll, 5000);
    };
    poll();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [selectedDeliveryId, dispatch]);

  const handleTrack = async (deliveryId) => {
    if (String(selectedDeliveryId) === String(deliveryId)) {
      setSelectedDeliveryId(null);
      return;
    }

    setSelectedDeliveryId(deliveryId);
  };

  useEffect(() => {
    const initialTrackDeliveryIds = [
      ...(Array.isArray(location.state?.trackDeliveryIds) ? location.state.trackDeliveryIds : []),
      location.state?.trackDeliveryId,
      ...readPendingTrackIds(),
    ].filter(Boolean).map(String);

    if (initialTrackDeliveryIds.length === 0 || initialTrackHandledRef.current || sortedDeliveries.length === 0) {
      return;
    }

    const matchingDelivery = sortedDeliveries.find(
      (delivery) => getDeliveryIdentityValues(delivery).some((identity) => initialTrackDeliveryIds.includes(identity))
    );

    if (!matchingDelivery) {
      return;
    }

    const matchedDeliveryId = getDeliveryIdentifier(matchingDelivery);

    initialTrackHandledRef.current = true;
    shouldFocusTrackingRef.current = true;
    window.sessionStorage.removeItem('pendingTrackDeliveryIds');
    window.sessionStorage.removeItem('pendingTrackDeliveryId');
    setSelectedDeliveryId(matchedDeliveryId);
  }, [dispatch, location.state, sortedDeliveries]);

  useEffect(() => {
    if (!selectedDeliveryId || !trackingData || !inlineTrackingRef.current) {
      return;
    }

    if (!shouldFocusTrackingRef.current) {
      return;
    }

    const trackingElement = inlineTrackingRef.current;
    const focusTrackingCard = () => {
      const targetTop = trackingElement.getBoundingClientRect().top + window.pageYOffset - 18;

      trackingElement.focus({ preventScroll: true });
      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth',
      });
    };

    window.requestAnimationFrame(() => {
      focusTrackingCard();
      window.setTimeout(focusTrackingCard, 250);
      window.setTimeout(focusTrackingCard, 700);
    });
    shouldFocusTrackingRef.current = false;
  }, [selectedDeliveryId, trackingData]);

  const getStatusIcon = (status) => {
    if (status === 'DELIVERED') return <FaCheckCircle className="text-success" />;
    if (status === 'IN_TRANSIT') return <FaTruck className="text-info" />;
    return <FaMapMarkerAlt className="text-warning" />;
  };

  const renderTrackingDetails = (deliveryId) => {
    if (!selectedDeliveryId || !trackingData || String(selectedDeliveryId) !== String(deliveryId)) return null;

    const matchedDelivery = sortedDeliveries.find(
      (delivery) => String(getDeliveryIdentifier(delivery)) === String(selectedDeliveryId)
    );
    const trackingPayment = {
      ...trackingData,
      paymentMethod: trackingData.paymentMethod || matchedDelivery?.paymentMethod,
      paymentStatus: trackingData.paymentStatus || matchedDelivery?.paymentStatus,
    };
    const displayOrderId = formatDisplayOrderId(trackingData.orderId);
    const resolvedDriverVehicleNumber =
      trackingData.driverVehicle ||
      matchedDelivery?.driverVehicle ||
      matchedDelivery?.vehicleNumber ||
      '--';
    const trackingCost = getDisplayCost(
      trackingData.actualCost,
      trackingData.estimatedCost,
      trackingData.totalCost,
      trackingData.amount,
      trackingData.price,
      trackingData.fare,
      matchedDelivery?.actualCost,
      matchedDelivery?.estimatedCost,
      matchedDelivery?.totalCost,
      matchedDelivery?.amount,
      matchedDelivery?.price,
      matchedDelivery?.fare
    );

    return (
      <Card
        id={`tracking-details-${deliveryId}`}
        data-tracking-details-card="true"
        className="shadow tracking-detail-shell inline-tracking-detail"
        ref={inlineTrackingRef}
        tabIndex="-1"
      >
        <Card.Header className="bg-primary text-white tracking-detail-header">
          <div className="tracking-detail-title-wrap">
            <div>
              <span className="tracking-live-pill">Live</span>
              <Card.Title className="mb-0">Tracking Details</Card.Title>
            </div>
            <p className="mb-0 tracking-detail-copy">{getStatusCopy(trackingData.status)}</p>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="tracking-detail-stats mb-4">
            <div className="detail-stat-card">
              <span>Status</span>
              <div><DeliveryStatusBadge status={trackingData.status} /><PaymentDetails order={trackingPayment} /></div>
            </div>
            <div className="detail-stat-card">
              <span>Vehicle</span>
              <strong>{trackingData.vehicleType}</strong>
            </div>
            <div className="detail-stat-card">
              <span>Estimated Cost</span>
              <strong>Rs {trackingCost}</strong>
            </div>
          </div>

          <Row>
            <Col md={6}>
              <div className="tracking-info-panel mb-4 mb-md-0">
                <h6 className="mb-3">Order Information</h6>
                <ul className="list-unstyled tracking-info-list">
                  {displayOrderId ? (
                    <li className="mb-2">
                      <strong>Order ID:</strong> {displayOrderId}
                    </li>
                  ) : null}
                  <li className="mb-2">
                    <strong>Status:</strong> <DeliveryStatusBadge status={trackingData.status} />
                  </li>
                  <li className="mb-2">
                    <strong>Vehicle:</strong> {trackingData.vehicleType}
                  </li>
                </ul>

                {trackingData.driverName && (
                  <>
                    <h6 className="mb-3 mt-4">Driver Information</h6>
                    <ul className="list-unstyled tracking-info-list">
                      <li className="mb-2">
                        <FaUser className="text-primary me-2" />
                        <strong>Name:</strong> {trackingData.driverName}
                      </li>
                      <li className="mb-2">
                        <FaPhone className="text-success me-2" />
                        <strong>Phone:</strong> {trackingData.driverPhone || 'N/A'}
                      </li>
                      <li className="mb-2">
                        <FaCar className="text-info me-2" />
                        <strong>Vehicle Number:</strong> {resolvedDriverVehicleNumber}
                      </li>
                    </ul>
                  </>
                )}

                {trackingData.pickupLocation && (
                  <div className="tracking-route-stack mt-3">
                    <div className="tracking-route-node pickup-node">
                      <small className="text-muted">Pickup</small>
                      <p className="mb-1"><FaMapMarkerAlt className="text-success me-2" />{trackingData.pickupLocation.name}</p>
                    </div>
                    <div className="tracking-route-node drop-node">
                      <small className="text-muted">Drop</small>
                      <p className="mb-0"><FaMapMarkerAlt className="text-danger me-2" />{trackingData.dropLocation?.name}</p>
                    </div>
                  </div>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="tracking-timeline-panel">
                <div className="timeline-panel-head mb-3">
                  <h6 className="mb-1">Timeline</h6>
                  <small className="text-muted">Latest delivery milestones</small>
                </div>
                <div className="timeline">
                  {trackingData.timeline?.map((event, index) => (
                    <div key={index} className="timeline-item mb-3">
                      <div className="timeline-marker">
                        <span className={`badge ${getTimelineBadgeClass(event.status)}`}>{index + 1}</span>
                      </div>
                      <div className="timeline-content-card">
                        <strong>{event.status || event.eventType}</strong>
                        <p className="text-muted small mb-0">
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                        {(event.description || event.details) && (
                          <small className="text-muted">{event.description || event.details}</small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          {!trackingData.driverLocation && (
            <Row className="mt-4">
              <Col>
                <Card className="shadow-sm tracking-map-card">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">
                      <FaMapMarkerAlt className="text-primary me-2" />
                      Driver Location
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <DriverLocationMap
                      driverLocation={trackingData.driverLocation}
                      pickupLocation={trackingData.pickupLocation}
                      dropLocation={trackingData.dropLocation}
                      driverName={trackingData.driverName}
                      vehicleType={trackingData.vehicleType}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {trackingData.driverLocation && (
            <Row className="mt-4">
              <Col>
                <Card className="shadow-sm">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">
                      <FaMapMarkerAlt className="text-primary me-2" />
                      Driver Location
                    </h6>
                  </Card.Header>
                  <Card.Body className="text-center py-4">
                    <FaMapMarkerAlt className="fa-3x mb-3 text-primary" />
                    <p className="mb-3">
                      <strong>{trackingData.driverName || 'Driver'}</strong> • {trackingData.vehicleType || 'Vehicle'}<br />
                      <small className="text-muted">Last updated: {new Date(trackingData.driverLocation.lastUpdated).toLocaleTimeString()}</small>
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => {
                        const { lat, lng } = trackingData.driverLocation;
                        const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}&ll=${lat},${lng}&z=15`;
                        window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="px-4 py-2"
                    >
                      <FaMapMarkerAlt className="me-2" />
                      View on Google Maps
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (loading && deliveries.length === 0) {
    return (
      <div className="delivery-list-container">
        <DeliveryOverviewStrip deliveries={[]} loading />
        {view === 'tracking' ? <TrackingSkeleton /> : <HistorySkeleton />}
      </div>
    );
  }

  if (error && deliveries.length === 0) {
    return (
      <Alert variant="danger">
        Error: {error}{' '}
        <Button variant="outline-danger" size="sm" disabled={loading} onClick={() => dispatch(fetchUserDeliveries())}>
          {loading ? 'Loading...' : 'Retry'}
        </Button>
      </Alert>
    );
  }

  if (deliveries.length === 0) {
    return (
      <div className="delivery-list-container">
        <div className="empty-state-card">
          <div className="empty-state-icon">{view === 'tracking' ? '🛰️' : '📦'}</div>
          <h4>{view === 'tracking' ? 'No active deliveries to track' : 'No delivery history yet'}</h4>
          <p>
            {view === 'tracking'
              ? 'Once a delivery is created, live status and driver details will appear here automatically.'
              : 'Your finished and in-progress deliveries will appear here as soon as you start booking.'}
          </p>
          <div className="empty-state-pills">
            <span>Live status</span>
            <span>Driver details</span>
            <span>Timeline view</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="delivery-list-container">
      <DeliveryOverviewStrip deliveries={sortedDeliveries} />

      {view === 'history' ? (
        <>
          <div className="list-hero mb-4">
            <div>
              <span className="list-hero-kicker">Archive</span>
              <h5 className="mb-1">Delivery History</h5>
              <p className="text-muted mb-0">Review previous orders and jump into tracking without leaving the page.</p>
            </div>
          </div>

          <div className="table-responsive history-desktop-table">
            <Table hover className="delivery-table">
              <thead className="table-dark">
                <tr>
                  <th>Delivery ID</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Status</th>
                  <th>Vehicle</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedDeliveries.map((delivery) => {
                  const deliveryId = getDeliveryIdentifier(delivery);
                  const isSelected = String(selectedDeliveryId) === String(deliveryId);

                  return (
                    <React.Fragment key={deliveryId}>
                      <tr className={isSelected ? 'is-expanded' : ''}>
                        <td>
                          <strong>#{String(deliveryId).slice(0, 8)}</strong>
                        </td>
                        <td>
                          <small>{delivery.pickupLocation?.name || 'N/A'}</small>
                        </td>
                        <td>
                          <small>{delivery.dropLocation?.name || 'N/A'}</small>
                        </td>
                        <td><DeliveryStatusBadge status={delivery.status} /><PaymentDetails order={delivery} /></td>
                        <td>{delivery.vehicleType}</td>
                        <td>
                          <strong>Rs {getDisplayCost(delivery.actualCost, delivery.estimatedCost, delivery.amount, delivery.price, delivery.fare)}</strong>
                        </td>
                        <td>
                          <div className="text-nowrap">
                            {isDeliveredStatus(delivery.status) ? (
                              <Badge bg="success">Completed</Badge>
                            ) : (
                              <Button
                                variant={isSelected ? 'primary' : 'outline-primary'}
                                size="sm"
                                className="track-toggle-btn"
                                onClick={() => handleTrack(deliveryId)}
                              >
                                {isSelected ? 'Tracking' : 'Track'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isSelected && (
                        <tr className="tracking-accordion-row">
                          <td colSpan={7}>
                            {renderTrackingDetails(deliveryId)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="history-mobile-stack">
            {sortedDeliveries.map((delivery) => {
              const deliveryId = getDeliveryIdentifier(delivery);
              const isSelected = String(selectedDeliveryId) === String(deliveryId);

              return (
                <Card className={`history-mobile-card ${isSelected ? 'is-expanded' : ''}`} key={`mobile-${deliveryId}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                      <div>
                        <span className="mobile-card-kicker">Order</span>
                        <h6 className="mb-1">#{String(deliveryId).slice(0, 8)}</h6>
                        <small className="text-muted">{delivery.vehicleType}</small>
                      </div>
                      <DeliveryStatusBadge status={delivery.status} /><PaymentDetails order={delivery} />
                    </div>

                    <div className="mobile-route-card mb-3">
                      <div>
                        <small className="text-muted d-block">Pickup</small>
                        <strong>{delivery.pickupLocation?.name || 'N/A'}</strong>
                      </div>
                      <FaArrowRight className="text-muted" />
                      <div>
                        <small className="text-muted d-block">Drop</small>
                        <strong>{delivery.dropLocation?.name || 'N/A'}</strong>
                      </div>
                    </div>

                    <div className="mobile-card-meta mb-3">
                      <span><FaBoxOpen className="me-2 text-warning" />{delivery.vehicleType}</span>
                      <span><FaClock className="me-2 text-info" />{new Date(delivery.createdAt).toLocaleDateString()}</span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <strong className="text-primary">Rs {getDisplayCost(delivery.actualCost, delivery.estimatedCost, delivery.amount, delivery.price, delivery.fare)}</strong>
                      {isDeliveredStatus(delivery.status) ? (
                        <Badge bg="success">Completed</Badge>
                      ) : (
                        <Button
                          variant={isSelected ? 'primary' : 'outline-primary'}
                          size="sm"
                          className="history-track-btn track-toggle-btn"
                          onClick={() => handleTrack(deliveryId)}
                        >
                          {isSelected ? 'Tracking' : 'Track'}
                        </Button>
                      )}
                    </div>

                    {isSelected && renderTrackingDetails(deliveryId)}
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="list-hero mb-4 tracking-hero">
            <div>
              <span className="list-hero-kicker">Live Tracking</span>
              <h5 className="mb-1">Track Your Deliveries</h5>
              <p className="text-muted mb-0">Follow order progress, assigned driver details, and timeline events in one premium view.</p>
            </div>
          </div>

          <Row className="g-3">
            {sortedDeliveries.map((delivery) => {
              const deliveryId = getDeliveryIdentifier(delivery);
              const isSelected = String(selectedDeliveryId) === String(deliveryId);

              return (
                <Col md={6} lg={4} key={deliveryId}>
                  <Card className={`tracking-card h-100 shadow-sm ${isSelected ? 'is-selected' : ''}`}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h6 className="mb-1">#{String(deliveryId).slice(0, 8)}</h6>
                          <small className="text-muted">{delivery.vehicleType}</small>
                        </div>
                        <div>{getStatusIcon(delivery.status)}</div>
                      </div>

                      <div className="mb-2">
                        <DeliveryStatusBadge status={delivery.status} /><PaymentDetails order={delivery} />
                      </div>

                      <hr />

                      <div className="mb-2">
                        <small className="text-muted">Pickup</small>
                        <p className="mb-2">
                          <FaMapMarkerAlt className="text-primary me-2" />
                          {delivery.pickupLocation?.name || 'N/A'}
                        </p>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted">Delivery</small>
                        <p className="mb-0">
                          <FaMapMarkerAlt className="text-success me-2" />
                          {delivery.dropLocation?.name || 'N/A'}
                        </p>
                      </div>

                      <Button
                        variant="primary"
                        size="sm"
                        className="w-100 tracking-detail-btn"
                        onClick={() => handleTrack(deliveryId)}
                      >
                        {isSelected ? 'Showing Details' : 'View Details'}
                      </Button>

                      {isSelected && renderTrackingDetails(deliveryId)}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </div>
  );
};

export default DeliveryList;
